import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from '../../common/withRouter';
import {
  ButtonOBSOLETED,
  Notification,
} from "../../web.common";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import {
  Filters,
  FilterInput,
  FilterSelect,
} from "./Filters";
import { getQueryStringParameters } from "../../common/utils";
import { DisplayGuid } from "../items";
import AddEntityButton from "../misc/AddEntityButton";

const AreasInternal = (props) => {
  const { history, location } = props;
  const [loadedData, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigationButtonStyle = {
    width: "6%",
    color: "grey",
    fontSize: "0.85rem",
  };

  const clickable = {
    cursor: "pointer"
  };

  useEffect(() => {
    async function loadFromApi() {
      loadData();
    }
    if (!loadedData) {
      loadFromApi();
    }
  });

  const loadData = async () => {
    setIsLoading(false);
    const filters = getQueryStringParameters(location.search);
    try {
      const data = await props.api.areas.searchAreas({
        filters
      });
      setData(data);
      setIsLoading(true);
    } catch (e) {
      console.error('Error loading areas:', e);
      setData({ areas: [], pages: 0, totalCount: 0 });
      setIsLoading(true);
    }
  };

  const filters = getQueryStringParameters(location.search);
  const page = parseInt(filters.page) || 1;
  const orderBy = filters.orderBy || "name";
  const orderDirection = filters.orderDirection || "asc";

  const orderHandler = (name) => {
    let direction;
    if (name.toLowerCase() === orderBy.toLowerCase()) {
      direction = orderDirection === "desc" ? "asc" : "desc";
    } else {
      direction = "asc";
    }

    Filters.goToPage(history, location, page, name, direction);
  };

  const pagingHandler = async (action) => {
    if (page < loadedData.pages && action === "add") {
      Filters.goToPage(history, location, page + 1, orderBy, orderDirection);
    }
    if (page > 1 && action === "sub") {
      Filters.goToPage(history, location, page - 1, orderBy, orderDirection);
    }
  };

  const mappedIsMainArea = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
  ];

  const mappedIsSuperArea = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
  ];

  const header = [
    {
      name: "AreaId",
      label: "Id",
      canOrder: false,
      breakWord: false,
      render: ({ areaId }) => <DisplayGuid>{areaId}</DisplayGuid>,
    },
    {
      name: "Name",
      label: "Name",
      canOrder: true,
      breakWord: false,
      render: ({ name }) => name,
    },
    {
      name: "Slug",
      label: "Slug",
      canOrder: true,
      breakWord: false,
      render: ({ slug }) => slug,
    },
    {
      name: "CountryCode",
      label: "Country",
      canOrder: true,
      breakWord: false,
      render: ({ countryCode }) => countryCode || "-",
    },
    {
      name: "IsMainArea",
      label: "Main Area",
      canOrder: true,
      breakWord: false,
      render: ({ isMainArea }) => isMainArea ? "Yes" : "No",
    },
    {
      name: "IsSuperArea",
      label: "Super Area",
      canOrder: true,
      breakWord: false,
      render: ({ isSuperArea }) => isSuperArea ? "Yes" : "No",
    },
    {
      name: "ZipRange",
      label: "Zip Range",
      canOrder: false,
      breakWord: false,
      render: ({ zipFrom, zipTo }) => {
        if (zipFrom && zipTo) {
          return `${zipFrom} - ${zipTo}`;
        }
        if (zipFrom) {
          return `From ${zipFrom}`;
        }
        if (zipTo) {
          return `To ${zipTo}`;
        }
        return "-";
      },
    },
    {
      name: "Position",
      label: "Position",
      canOrder: false,
      breakWord: false,
      render: ({ position }) => {
        if (position && position.hasValue) {
          return `${position.latitude.toFixed(4)}, ${position.longitude.toFixed(4)}`;
        }
        return "-";
      },
    },
  ];

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <PageTop
            documentTitle="Areas"
            documentDescription="List of all areas"
          />

          <h2 className="main-page-header">
            Areas
            <AddEntityButton to='/area' />
          </h2>

          <Filters
            initFromUrl
            submitUrl
            validateForm={(
              {
                id,
                name,
                slug,
                countryCode,
                isMainArea,
                isSuperArea,
                zipFrom,
                zipTo
              },
              updatedKey
            ) => {
              if (updatedKey !== "id") {
                return {
                  id,
                  name,
                  slug,
                  countryCode,
                  isMainArea,
                  isSuperArea,
                  zipFrom,
                  zipTo
                };
              }
              return { id };
            }}
            render={(formData, update) => (
              <div className="filters-block">
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="id">
                    Area Id
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="id"
                    placeholder="Find by id"
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="name">
                    Name
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="name"
                    placeholder="Find by name"
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="slug">
                    Slug
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="slug"
                    placeholder="Find by slug"
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="countryCode">
                    Country Code
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="countryCode"
                    placeholder="Find by country code"
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="isMainArea">
                    Is Main Area
                  </label>
                  <FilterSelect
                    className='filter-select'
                    data={formData}
                    update={update}
                    name='isMainArea'
                    options={mappedIsMainArea}
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="isSuperArea">
                    Is Super Area
                  </label>
                  <FilterSelect
                    className='filter-select'
                    data={formData}
                    update={update}
                    name='isSuperArea'
                    options={mappedIsSuperArea}
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="zipFrom">
                    Zip From
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="zipFrom"
                    type="number"
                    placeholder="Zip from"
                  />
                </div>
                <div className="filters-label-block">
                  <label className="filters-label" htmlFor="zipTo">
                    Zip To
                  </label>
                  <FilterInput
                    data={formData}
                    update={update}
                    name="zipTo"
                    type="number"
                    placeholder="Zip to"
                  />
                </div>
                <br />
                <br />
                <div className="filters-submit-wrapper">
                  <ButtonOBSOLETED
                    type="submit"
                    buttonContent="Search"
                    disabled={!isLoading}
                  />
                </div>
                <hr />
              </div>
            )}
          />

          {loadedData == null ? (
            <span>Loading...</span>
          ) : (
            <>
              {loadedData.areas && loadedData.areas.length > 0 ? (
                <React.Fragment>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <input
                      style={navigationButtonStyle}
                      type="button"
                      value="< Prev"
                      onClick={() => pagingHandler("sub")}
                    />
                    <span style={{ color: "grey", fontSize: "0.85rem" }}>
                      Showing page {page} out of {loadedData.pages}
                    </span>
                    <input
                      style={navigationButtonStyle}
                      type="button"
                      value="Next >"
                      onClick={() => pagingHandler("add")}
                    />
                  </div>
                  <br />

                  <article className="data-table-wrap">
                    <table className="data-lists component-data-table cell-content-align-top">
                      <thead>
                        <tr>
                          {header.map((property) => (
                            <td
                              key={property.name}
                              style={property.canOrder ? clickable : null}
                              onClick={() =>
                                property.canOrder
                                  ? orderHandler(property.name)
                                  : null
                              }
                            >
                              {property.label}{" "}
                              {orderBy.toLowerCase() === property.name.toLowerCase()
                                ? orderDirection === "desc"
                                  ? "↓"
                                  : "↑"
                                : ""}
                            </td>
                          ))}
                        </tr>
                      </thead>
                      {loadedData.areas.map((area) => (
                        <tbody key={area.areaId}>
                          <tr
                            className="hover-listview"
                            style={{ cursor: 'pointer' }}
                            onClick={() => history.push(`/area/${area.areaId}`)}
                          >
                            {header.map((property) => (
                              <td key={property.name} style={property.breakWord ? {overflowWrap: 'anywhere'} : {}}>
                                <strong className="table-cell-label">
                                  {property.label}
                                </strong>
                                {property.render(area)}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      ))}
                    </table>
                  </article>

                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <input
                      style={navigationButtonStyle}
                      type="button"
                      value="< Prev"
                      onClick={() => pagingHandler("sub")}
                    />
                    <input
                      style={navigationButtonStyle}
                      type="button"
                      value="Next >"
                      onClick={() => pagingHandler("add")}
                    />
                  </div>
                </React.Fragment>
              ) : (
                <div style={{ padding: "20px", textAlign: "center", color: "grey" }}>
                  No areas found matching your search criteria.
                </div>
              )}
            </>
          )}
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

AreasInternal.propTypes = {
  api: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  isLoggedIn: PropTypes.bool,
};

const Areas = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(AreasInternal)
  )
);

Areas.requiresLogin = true;

export default Areas;
